import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { PickerComponent } from '@ctrl/ngx-emoji-mart';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { tap } from 'rxjs';

import { MatIconButton } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBar } from '@angular/material/progress-bar';

import { ChatStateService } from '@lib/chat/services/chat-state.service';
import { ChatMessage, ThreadType } from '@lib/chat/models/chat.interface';
import { ChatDraft } from '@lib/chat/models/chat.state.interface';
import { ChatCommunicationService } from '@lib/chat/services/chat-communication.service';
import { clearChatDraftByThread, setChatDraft } from '@lib/chat/store/chat.actions';
import { selectChatDrafts } from '@lib/chat/store/chat.selectors';

import { MemberProfile } from '@lib/core/models/member.interface';

import { ChatMessageComponent } from './chat-message/chat-message.component';

@Component({
  selector: 'chat-messages-list',
  standalone: true,
  imports: [ChatMessageComponent, FormsModule, MatFormFieldModule, MatIconButton, MatInputModule, ReactiveFormsModule, MatProgressBar, MatIcon, PickerComponent, TranslatePipe],
  templateUrl: './chat-messages-list.component.html',
  styles: `
    :host {
      --mdc-linear-progress-track-color: #547af3;
      @apply h-full;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMessagesListComponent implements OnInit, AfterViewInit, OnChanges {
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly chatService = inject(ChatCommunicationService);
  private readonly chatState = inject(ChatStateService);
  private readonly store = inject(Store);

  //as HTML element to call scroll to end on container
  @ViewChild('messageContainer') messageContainer: ElementRef;
  @ViewChild('scrollTick') scrollTick: ElementRef;
  @ViewChild('scrollEndTick') scrollEndTick: ElementRef;
  @ViewChild('fileInput') fileInput: ElementRef;

  protected startObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        if (this.innerMessages().length) {
          //TODO: there is a bug that when messages ended it will trigger double loading of the list
          if (this.innerMessages()[0].id === this.firstId) return;
          this.firstId = this.innerMessages()[0].id;
          this.getMessagesBefore(this.innerMessages()[0].createdAt);
        }
      }
    });
  });

  protected endObserver = new IntersectionObserver(entries => entries);

  loading = input.required<boolean>();
  threadId = input.required<string>();
  ownUserId = input.required<string>();
  threadUserId = input.required<string>();
  clientProfile = input.required<MemberProfile>();
  ownerName = input<string>();
  messages = input.required<ChatMessage[]>();
  unreadCount = input<number>(0);
  isAssignedToMe = input<boolean>(false);

  protected readonly hasTabsFeature = computed<boolean>(() => this.chatState.features().showThreadTabs);

  protected readonly innerMessages = computed<ChatMessage[]>(() => {
    if (this.firstId) {
      setTimeout(() => {
        this.messageContainer.nativeElement.querySelector(`[data-id="${this.firstId}"]`).scrollIntoView();
        this.firstId = null;
      }, 0);
    }
    return this.messages().sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  });

  protected readonly getUnreadMarkerPosition = computed<number>(() => {
    if (this.unreadCount() === 0) return -1;

    const messages = this.innerMessages();
    const ownUserId = this.ownUserId();
    let incomingUnreadCount = 0;
    const totalUnread = this.unreadCount();

    for (let i = messages.length - 1; i >= 0 && incomingUnreadCount < totalUnread; i--) {
      const message = messages[i];
      const isMyMessage = (message.userId === '00000000-0000-0000-0000-000000000000' || message.userId === ownUserId) && message.metadata?.['isLilo'] === 'true';

      if (!isMyMessage) {
        incomingUnreadCount++;

        if (incomingUnreadCount === totalUnread) return i;
      }
    }
    return messages.length > 0 ? 0 : -1;
  });

  protected readonly hasUnreadMessages = computed<boolean>(() => this.unreadCount() > 0);

  protected readonly extractedPhoneNumber = computed<string>(() => {
    const ownerName = this.ownerName();
    const phoneMatch = ownerName.match(/\d+/);

    if (!ownerName) return '';
    else return phoneMatch ? phoneMatch[0] : ownerName;
  });

  protected tabSelect = output<ThreadType>();
  protected sendMessage = output<string | FormData>();
  protected messagesBefore = output<Date>();
  protected messagesAfter = output<Date>();
  protected markRead = output<void>();
  protected isIntersecting = output<boolean>();
  protected retryMessage = output<string>();
  protected takeIntoWork = output<void>();
  protected completeWork = output<void>();

  protected messageTextFc: FormControl<string | null>;
  protected readonly selectedFile = signal<File | null>(null);
  protected readonly showEmojis = signal<boolean>(false);

  protected firstId?: string;

  ngOnInit() {
    this.initMessageTextFc();
  }

  ngOnChanges(changes: SimpleChanges) {
    const messagesChange = changes['messages'];
    const isChanged = messagesChange && messagesChange.currentValue?.length && messagesChange.currentValue?.length !== messagesChange.previousValue?.length;
    if (!this.firstId && isChanged) {
      this.scrollToBottom();
    }
  }

  ngAfterViewInit() {
    this.startObserver.observe(this.scrollTick.nativeElement);
    this.endObserver.observe(this.scrollEndTick.nativeElement);
  }

  initMessageTextFc() {
    const existingDraft = computed(() => {
      const drafts = this.store.selectSignal(selectChatDrafts)();

      return drafts.find(draft => draft.threadId === this.threadId());
    });
    this.messageTextFc = this.fb.control(existingDraft()?.message || null);

    this.messageTextFc.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      const trimmedValue = value?.trim();
      const chatDraft: ChatDraft = {
        threadId: this.threadId(),
        message: trimmedValue,
      };

      if (trimmedValue) this.store.dispatch(setChatDraft({ chatDraft }));
      else this.store.dispatch(clearChatDraftByThread({ chatDraft }));
    });
  }

  onSendMessage() {
    if (!this.messageTextFc.valid) return;
    const message = this.messageTextFc.value.trim();
    const formData = new FormData();

    // TODO: implement multiple files parsing and setting their links to message.media
    if (this.selectedFile() !== null) {
      formData.append('name', this.selectedFile().name);
      formData.append('files', this.selectedFile());

      this.chatService
        .uploadFiles(formData)
        .pipe(tap(link => formData.append('link', link[this.selectedFile().name])))
        .subscribe({
          next: () => this.sendMessage.emit(formData),
          complete: () => this.selectedFile.set(null),
        });
    } else {
      formData.append('message', message);
      if (message) this.sendMessage.emit(formData.get('message') as string);
    }

    this.showEmojis.set(false);
    this.messageTextFc.reset();
  }

  getMessagesBefore(timestamp: Date) {
    this.messagesBefore.emit(timestamp);
  }

  private scrollToBottom() {
    setTimeout(() => {
      const lastElement = this.messageContainer.nativeElement.querySelector(`[data-index="${this.innerMessages().length - 1}"]`);
      if (lastElement) lastElement.scrollIntoView({ behavior: 'instant' });
    }, 0);
  }

  protected navigateToClientProfile(memberId: string) {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/members', memberId, 'profile']));

    window.open(url, '_blank');
  }

  protected openFileBuffer() {
    this.fileInput.nativeElement.click();
  }

  onFileSelected($event: any) {
    const file: File = $event.target.files[0];
    if (file) {
      const reader = new FileReader();

      this.messageTextFc.setValue(file.name);
      this.selectedFile.set(file);

      reader.onload = () => {};
      reader.readAsArrayBuffer(file);
    }
  }

  protected toggleEmojis() {
    this.showEmojis.set(!this.showEmojis());
  }

  protected selectEmoji($event: any, messageInput: HTMLTextAreaElement) {
    const emoji = $event.emoji.native;
    const currentMessageText = this.messageTextFc.value ?? '';
    const startPosition = messageInput.selectionStart;
    const endPosition = messageInput.selectionEnd;
    const finalText = currentMessageText.substring(0, startPosition) + emoji + currentMessageText.substring(endPosition);

    this.messageTextFc.setValue(finalText);

    // Set cursor position after the inserted emoji
    setTimeout(() => {
      const cursorPosition = startPosition + emoji.length;
      messageInput.setSelectionRange(cursorPosition, cursorPosition);
      messageInput.focus();
    }, 0);
  }

  protected onMarkAsRead() {
    this.markRead.emit();
  }

  protected onRetryMessage(messageId: string) {
    this.retryMessage.emit(messageId);
  }

  protected onTakeIntoWork() {
    this.takeIntoWork.emit();
  }

  protected onCompleteWork() {
    this.completeWork.emit();
  }
}
